import React, { useState, useEffect, useContext } from "react";
import axios from "../../axiosConfig";
import "../../styles/TrainerWorkingHours.css";
import { TrainerContext } from "../../context/TrainerContext";

const TrainerWorkingHoursContent = () => {
    const { trainer } = useContext(TrainerContext);
    const [workingHours, setWorkingHours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isEditing, setIsEditing] = useState(false);

    // Days in English + Polish labels
    const dayLabels = {
        MONDAY: "Poniedziałek",
        TUESDAY: "Wtorek",
        WEDNESDAY: "Środa",
        THURSDAY: "Czwartek",
        FRIDAY: "Piątek",
        SATURDAY: "Sobota",
        SUNDAY: "Niedziela",
    };

    // Order for sorting: Monday -> Sunday
    const dayOrder = [
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
        "SATURDAY",
        "SUNDAY",
    ];

    useEffect(() => {
        if (!trainer?.id) return;

        const token = localStorage.getItem("jwt");
        axios
            .get(`/api/v1/trainer/get-working-hours/${trainer.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const data = res.data || [];
                if (data.length > 0) {
                    setWorkingHours(data);
                    setIsEditing(true);
                } else {
                    setWorkingHours([]);
                    setIsEditing(false);
                }
            })
            .catch((err) => {
                setError("Nie udało się pobrać godzin pracy");
            })
            .finally(() => setLoading(false));
    }, [trainer]);

    // Sort workingHours from Monday to Sunday
    const sortedWorkingHours = [...workingHours].sort(
        (a, b) => dayOrder.indexOf(a.dayOfWeek) - dayOrder.indexOf(b.dayOfWeek)
    );

    // Determine which days are missing
    const missingDays = dayOrder.filter(
        (dayKey) => !workingHours.some((wh) => wh.dayOfWeek === dayKey)
    );

    // Handle input changes
    const handleChange = (dayKey, field, value) => {
        setWorkingHours((prev) =>
            prev.map((h) =>
                h.dayOfWeek === dayKey
                    ? { ...h, [field]: value }
                    : h
            )
        );
    };

    // Add a new day with empty times
    const handleAddDay = (dayKey) => {
        setWorkingHours((prev) => [
            ...prev,
            { dayOfWeek: dayKey, openingTime: "", closingTime: "" },
        ]);
    };

    // Remove a day
    const handleRemoveDay = (dayKey) => {
        setWorkingHours((prev) => prev.filter((h) => h.dayOfWeek !== dayKey));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Only send entries that have both opening and closing times
        const requestBody = workingHours.filter(
            (h) => h.openingTime && h.closingTime
        );

        if (requestBody.length === 0) {
            setError("Proszę podać co najmniej jedną godzinę pracy.");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");
            if (isEditing) {
                await axios.put("/api/v1/trainer/update-working-hours", requestBody, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSuccess("Godziny pracy zaktualizowane pomyślnie!");
            } else {
                await axios.post("/api/v1/trainer/add-working-hours", requestBody, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSuccess("Godziny pracy dodane pomyślnie!");
                setIsEditing(true);
            }
        } catch (err) {
            setError("Błąd podczas zapisywania godzin pracy.");
        }
    };

    if (loading) {
        return <div className="loading">Ładowanie godzin pracy...</div>;
    }

    return (
        <div className="working-hours-container">
            {/* Messages */}
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Working Hours Form */}
            <form className="working-hours-form" onSubmit={handleSubmit}>
                {sortedWorkingHours.length > 0 ? (
                    <div className="hours-grid">
                        {sortedWorkingHours.map(({ dayOfWeek, openingTime, closingTime }) => (
                            <div key={dayOfWeek} className="day-card">
                                <div className="day-header">
                                    <h3 className="day-name">{dayLabels[dayOfWeek]}</h3>
                                    <button
                                        type="button"
                                        className="remove-day-btn"
                                        onClick={() => handleRemoveDay(dayOfWeek)}
                                        title="Usuń dzień"
                                    >
                                        ✕
                                    </button>
                                </div>
                                <div className="time-inputs">
                                    <div className="input-group">
                                        <label>Otwarcie</label>
                                        <input
                                            type="time"
                                            value={openingTime}
                                            onChange={(e) =>
                                                handleChange(dayOfWeek, "openingTime", e.target.value)
                                            }
                                            className="time-input"
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Zamknięcie</label>
                                        <input
                                            type="time"
                                            value={closingTime}
                                            onChange={(e) =>
                                                handleChange(dayOfWeek, "closingTime", e.target.value)
                                            }
                                            className="time-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-hours">
                        <div className="no-hours-icon">🕒</div>
                        <h3>Brak godzin pracy</h3>
                        <p>Dodaj dni pracy, aby ustalić swój harmonogram.</p>
                    </div>
                )}

                {/* Save Button */}
                {sortedWorkingHours.length > 0 && (
                    <div className="button-group">
                        <button type="submit" className="btn btn-primary">
                            {isEditing ? "Zapisz zmiany" : "Dodaj godziny pracy"}
                        </button>
                    </div>
                )}
            </form>

            {/* Add Missing Days */}
            {missingDays.length > 0 && (
                <div className="missing-days-section">
                    <div className="section-title">Dodaj dni pracy</div>
                    <div className="missing-days-grid">
                        {missingDays.map((dayKey) => (
                            <button
                                key={dayKey}
                                type="button"
                                onClick={() => handleAddDay(dayKey)}
                                className="add-day-btn"
                            >
                                <span className="add-icon">+</span>
                                {dayLabels[dayKey]}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainerWorkingHoursContent;